import React, { memo, useCallback, useContext, useMemo, useState } from "react";
import Box from "@mui/material/Box";
import Stack from "@mui/material/Stack";
import IconButton from "@mui/material/IconButton";
import PublishIcon from "@mui/icons-material/Publish";
import { TimelineGridContext, TimelineGridRow } from "../timelinePlanner/timelinePlannerGrid";
import { DateActions } from "./DateActions";
import { PlannedWorkShiftDto, PlannedWorkShiftState } from "../../models/plannedWorkShift/plannedWorkShift";
import { Route, useParams } from "react-router-dom";
import { WorkShiftTimelineCell } from "./WorkShiftTimelineCell";
import { OverridableComponent } from "@mui/material/OverridableComponent";
import { SvgIconTypeMap, Chip, Tooltip, Typography } from "@mui/material";
import { useTranslation } from "react-i18next";
import { usePublishShifts } from "../../hooks/workShiftPlanning/usePublishShifts";
import { useAppDispatch, useAppSelector } from "../../framework/customStore";
import { WorkShiftPlanningParams } from "./WorkShiftPlanningRouter";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import { toggleRowExpanded } from "../../store/workShiftPlanningSlice";
import { DayBookingItemBeta } from "../../models/dayBooking/dayBookingItemBeta";
import { DayBookingTypeItemBeta } from "../../models/dayBookingType/dayBookingTypeItemBeta";
import { EmployeeDayBookings } from "./dayBookings/EmployeeDayBookings";
import { AppUtils } from "../../models/common/appUtils";
import { Base } from "../../framework/base";
import { EmployeeListItemDto } from "../../models/employee/employee";
import { WorkShiftDtoBasic } from "../../models/workShift/workShift";

const GRID_CELL_HORIZONTAL_PADDING = 4;
const SHIFT_VERTICAL_GAP = 6;
const BASELINE_GAP = 4;
const DEFAULT_SHIFT_HEIGHT = 120;
const HEIGHT_CHANGE_EPSILON = 0.5;

type HeightMap = Record<string, number>;

const numbersAreClose = (a: number, b: number, epsilon = HEIGHT_CHANGE_EPSILON) =>
    Math.abs(a - b) < epsilon;

const hasMeaningfulHeightChange = (prev: HeightMap, next: HeightMap) => {
    const prevKeys = Object.keys(prev);
    const nextKeys = Object.keys(next);

    if (prevKeys.length !== nextKeys.length) {
        return true;
    }

    return nextKeys.some((key) => {
        const prevValue = prev[key] ?? 0;
        const nextValue = next[key] ?? 0;
        return !numbersAreClose(prevValue, nextValue);
    });
};

const calculateShiftTopPositions = (
    shifts: PlannedWorkShiftDto[] | undefined,
    columnContentHeights: HeightMap,
    shiftHeights: HeightMap,
    resolveRenderedDate: (shift: PlannedWorkShiftDto) => string
) => {
    if (!shifts || shifts.length === 0) {
        return new Map<string, number>();
    }

    const positions = new Map<string, number>();
    const placedShifts: Array<{
        start: number;
        end: number;
        top: number;
        bottom: number;
        renderedDate: string;
    }> = [];
    const sortedShifts = [...shifts].sort((a, b) => (a.startTime ?? "").localeCompare(b.startTime ?? ""));

    sortedShifts.forEach((shiftItem) => {
        const renderedDate = resolveRenderedDate(shiftItem);

        const baseline = columnContentHeights[renderedDate] ?? 0;
        const baselineGap = baseline > 0 ? BASELINE_GAP : 0;
        const measuredHeight = shiftHeights[shiftItem.id] ?? DEFAULT_SHIFT_HEIGHT;

        const parseTimestamp = (value?: string, fallback?: number) => {
            if (!value) {
                return fallback ?? 0;
            }
            const parsed = Date.parse(value);
            if (Number.isNaN(parsed)) {
                return fallback ?? 0;
            }
            return parsed;
        };

        const startMs = parseTimestamp(shiftItem.startTime);
        const endMs = parseTimestamp(shiftItem.endTime, startMs);

        const overlappingShifts = placedShifts.filter((placed) => {
            return placed.renderedDate === renderedDate;
        });

        let top = baseline + baselineGap;
        let bottom = top + measuredHeight;

        if (overlappingShifts.length > 0) {
            const requiredTop = overlappingShifts.reduce(
                (max, placed) => Math.max(max, placed.bottom),
                top
            );

            if (requiredTop > top) {
                top = requiredTop;
                bottom = top + measuredHeight;
            }
        }

        placedShifts.push({
            start: startMs,
            end: endMs,
            top,
            bottom: bottom + SHIFT_VERTICAL_GAP,
            renderedDate,
        });

        positions.set(shiftItem.id, top);
    });

    return positions;
};

const calculateRowHeight = (
    shifts: PlannedWorkShiftDto[] | undefined,
    columnContentHeights: HeightMap,
    shiftTopPositions: Map<string, number>,
    shiftHeights: HeightMap
) => {
    let maxHeight = 0;

    Object.values(columnContentHeights).forEach((height) => {
        maxHeight = Math.max(maxHeight, height);
    });

    shifts?.forEach((shiftItem) => {
        const top = shiftTopPositions.get(shiftItem.id);
        if (top === undefined) {
            return;
        }

        const height = shiftHeights[shiftItem.id] ?? DEFAULT_SHIFT_HEIGHT;
        maxHeight = Math.max(maxHeight, top + height);
    });

    return Math.ceil(maxHeight + SHIFT_VERTICAL_GAP);
};

const EMPTY_DAY_BOOKINGS: DayBookingItemBeta[] = [];
const EMPTY_DAY_BOOKING_TYPES: Record<string, DayBookingTypeItemBeta> = {};

interface WorkShiftTimelineRowProps {
    shifts: PlannedWorkShiftDto[];
    actualShifts?: WorkShiftDtoBasic[];
    selectedShiftIds?: string[];
    targetId: string;
    label: string;
    totalActualMinutes?: number;
    totalPlannedMinutes?: number;
    projectedActualMinutes?: number;
    Icon: OverridableComponent<SvgIconTypeMap<any, "svg">>;
    onShiftClick?: (shiftId: string, event: React.MouseEvent, shift: PlannedWorkShiftDto) => void;
    selectedCell?: { day: string; targetId: string } | null;
    onCellSelect?: (cell: { day: string; targetId: string }, event?: React.MouseEvent) => void;
    dayBookings?: DayBookingItemBeta[];
    dayBookingTypes?: Record<string, DayBookingTypeItemBeta>;
    employees: EmployeeListItemDto[];
    timelineStart: string;
    timelineEnd: string;
}

const WorkShiftChipTooltip = ({
    title,
    color,
    value,
    showDash = false
}: {
    title: string;
    color: "success" | "primary" | "warning";
    value?: number;
    showDash?: boolean;
}) => (
    <Tooltip title={title}>
        <Chip
            color={color}
            size="small"
            sx={{ minWidth: 50 }}
            label={
                value
                    ? AppUtils.getDurationStrByDurationMinShort(value, true)
                    : showDash ? <Typography>-</Typography> : ""
            }
        />
    </Tooltip>
);

export const WorkShiftTimelineRow = memo(
    ({
        shifts = [],
        actualShifts,
        targetId,
        label,
        totalActualMinutes,
        totalPlannedMinutes,
        projectedActualMinutes,
        Icon,
        selectedShiftIds = [],
        onShiftClick,
        onCellSelect,
        selectedCell,
        dayBookings,
        dayBookingTypes,
        employees,
        timelineStart,
        timelineEnd
    }: WorkShiftTimelineRowProps) => {
        const { t } = useTranslation();
        const { publishEntityShifts, saving } = usePublishShifts();
        const { tab } = useParams<WorkShiftPlanningParams>();
        const dispatch = useAppDispatch();

        const cardViewMode = useAppSelector(
            (state) => state.workShiftPlanning.cardViewMode
        );
        const rowExpanded = useAppSelector(
            (state) => state.workShiftPlanning.rowExpanded
        );
        const isRowExpanded =
            rowExpanded?.[targetId ?? "null"] ??
            (cardViewMode === "expanded");

        const allShifts = useAppSelector(
            (state) => state.workShiftPlanning.plannedWorkShifts
        );

        const hasPlannedShifts = shifts?.some(
            (s) => s.state === PlannedWorkShiftState.InPlanning
        );

        const handlePublishEntityShifts = () => {
            if (targetId && targetId !== "null") {
                void publishEntityShifts(allShifts, targetId, tab);
            }
        };

        const showPublishButton = targetId !== "null" && hasPlannedShifts;

        const resolvedDayBookings = dayBookings ?? EMPTY_DAY_BOOKINGS;
        const resolvedDayBookingTypes = dayBookingTypes ?? EMPTY_DAY_BOOKING_TYPES;
        const [columnContentHeights, setColumnContentHeights] = useState<HeightMap>({});
        const [shiftHeights, setShiftHeights] = useState<HeightMap>({});

        const handleColumnHeightsChange = useCallback((heights: Record<string, number>) => {
            setColumnContentHeights((prev) => {
                if (!hasMeaningfulHeightChange(prev, heights)) {
                    return prev;
                }

                return heights;
            });
        }, []);

        const handleShiftHeightChange = useCallback((shiftId: string, height: number) => {
            setShiftHeights((prev) => {
                const current = prev[shiftId];
                if (current !== undefined && numbersAreClose(current, height)) {
                    return prev;
                }

                return { ...prev, [shiftId]: height };
            });
        }, []);

        const timelineContext = useContext(TimelineGridContext);

        const resolveRenderedDate = useCallback(
            (shift: PlannedWorkShiftDto) => {
                const startDay = Base.dayjsToJsonDate(shift.startTime);

                if (!timelineContext) {
                    return startDay;
                }

                const dates = timelineContext.getGridDates();
                const columnIndex = timelineContext.getColumnForDate(shift.startTime);
                const resolvedDate = dates?.[Math.max(0, columnIndex - 1)];

                return resolvedDate ?? startDay;
            },
            [timelineContext]
        );

        const shiftTopPositions = useMemo(
            () => calculateShiftTopPositions(shifts, columnContentHeights, shiftHeights, resolveRenderedDate),
            [shifts, columnContentHeights, shiftHeights, resolveRenderedDate]
        );

        const rowHeight = useMemo(
            () => calculateRowHeight(shifts, columnContentHeights, shiftTopPositions, shiftHeights),
            [shifts, columnContentHeights, shiftTopPositions, shiftHeights]
        );

        return (
            <TimelineGridRow
                rowHeight={rowHeight}
                renderColumnHeader={(date: string) => (
                    <Route
                        render={(props) => (
                            <DateActions
                                {...props}
                                date={date}
                                targetId={targetId}
                                selectedCell={selectedCell}
                                onCellSelect={onCellSelect}
                                employees={employees}
                                timelineStart={timelineStart}
                                timelineEnd={timelineEnd}
                            />
                        )}
                    />
                )}
                cellSelection={
                    onCellSelect ? {
                        targetId,
                        selectedCell,
                        onCellSelect
                    } : undefined}
                infoColumn={
                    <Box sx={{ fontSize: "small", pt: 1, pb: 1 }}>
                        <Stack direction="row" justifyContent="space-between" alignItems="center">
                            <div className="bold flex align-items-center overflow-break">
                                <Icon
                                    sx={{ mr: 1, color: "primary.dark" }}
                                    fontSize="small"
                                />
                                <span>{label}</span>
                            </div>

                            <Stack direction="row" alignItems="center" spacing={0.5}>
                                {showPublishButton ? (
                                    <Tooltip title={t("workShiftPlanning.publishEntityShifts")}>
                                        <IconButton
                                            size="small"
                                            disabled={saving}
                                            onClick={handlePublishEntityShifts}
                                            color="primary"
                                        >
                                            <PublishIcon fontSize="small" />
                                        </IconButton>
                                    </Tooltip>
                                ) : null}
                                <Tooltip title={
                                    isRowExpanded
                                        ? t("workShiftPlanning.compact.collapseRow")
                                        : t("workShiftPlanning.compact.expandRow")
                                }
                                >
                                    <IconButton
                                        id={`workshift-timeline-row-toggle-expand-${label}`}
                                        size="small"
                                        onClick={() => dispatch(toggleRowExpanded(targetId ?? "null"))}
                                        color="primary"
                                    >
                                        {isRowExpanded ? (
                                            <ExpandLessIcon fontSize="small" />
                                        ) : (
                                            <ExpandMoreIcon fontSize="small" />
                                        )}
                                    </IconButton>
                                </Tooltip>
                            </Stack>
                        </Stack>
                        {(totalPlannedMinutes !== undefined || totalActualMinutes !== undefined) && (
                        <Stack direction="row" gap={1}>
                            {totalActualMinutes !== undefined && (
                                <WorkShiftChipTooltip
                                    title={t("workShiftPlanning.actualWorkhours")}
                                    color="success"
                                    value={totalActualMinutes}
                                    showDash={true}
                                />
                            )}
                            {totalPlannedMinutes !== undefined && (
                                <WorkShiftChipTooltip
                                    title={t("workShiftPlanning.plannedWorkhours")}
                                    color="primary"
                                    value={totalPlannedMinutes}
                                    showDash={true}
                                />
                            )}
                            {projectedActualMinutes !== undefined && (
                                <WorkShiftChipTooltip
                                    title={t("workShiftPlanning.projectedActualWorkhours")}
                                    color="warning"
                                    value={projectedActualMinutes}
                                />
                            )}
                        </Stack>
                        )}
                    </Box>
                }
            >
                {resolvedDayBookings.length > 0 ? (
                    <EmployeeDayBookings
                        bookings={resolvedDayBookings}
                        dayBookingTypes={resolvedDayBookingTypes}
                        onColumnHeightsChange={handleColumnHeightsChange}
                        employees={employees}
                    />
                ) : null}
                {shifts?.map((shift) => (
                    <WorkShiftTimelineCell
                        shift={shift}
                        actualShifts={actualShifts}
                        key={shift.id}
                        isSelected={selectedShiftIds.includes(shift.id)}
                        onClick={(e) => onShiftClick?.(shift.id, e, shift)}
                        isCompact={!isRowExpanded}
                        topOffset={shiftTopPositions.get(shift.id) ?? 0}
                        horizontalPadding={GRID_CELL_HORIZONTAL_PADDING}
                        onHeightChange={handleShiftHeightChange}
                    />
                ))}
            </TimelineGridRow>
        );
    }
);
